import React, { memo } from "react";
import { ChevronsLeftIcon, ChevronsRightIcon } from "lucide-react-native";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";

/**
 * Expanded width, as the closed set of panels the pitch draws (SE-8486).
 * `side-panel` is the frame's 344 px (281:2793, W04/W05); `add-players` is
 * the scenario's 400 px Add-players panel (529:25952, S04).
 */
export type CollapsiblePitchPanelWidth = "side-panel" | "add-players";

/** Props prepared by the collapsible panel container. */
export interface CollapsiblePitchPanelViewProps {
  readonly side: "left" | "right";
  readonly children: React.ReactNode;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly pointsLeft: boolean;
  readonly width: CollapsiblePitchPanelWidth;
}

/** Expanded width class per panel kind; 344 has no rung, so it is named. */
const EXPANDED_WIDTH: Record<CollapsiblePitchPanelWidth, string> = {
  "side-panel": "w-pitch-side-panel",
  "add-players": "w-100",
};

/**
 * Where the 28 px handle sits. Expanded, it straddles the panel's inner
 * border — half its own width past the edge (14 px, which the spacing scale
 * has no rung for; the percentage shift is exact). Collapsed there is no
 * border to straddle, so it moves fully onto the pitch side and stays whole.
 */
const HANDLE_PLACEMENT = {
  left: {
    expanded: "right-0 translate-x-1/2",
    collapsed: "right-0 translate-x-full",
  },
  right: {
    expanded: "left-0 -translate-x-1/2",
    collapsed: "left-0 -translate-x-full",
  },
} as const;

/**
 * Renders a pitch side panel while keeping its mounted content intact.
 * @param root0 - Prepared panel presentation.
 * @param root0.side - Edge of the pitch.
 * @param root0.children - Mounted panel content.
 * @param root0.expanded - Whether the panel is visible.
 * @param root0.onToggle - Toggle callback.
 * @param root0.pointsLeft - Direction of the rendered chevrons.
 * @param root0.width - Expanded width preset.
 */
const CollapsiblePitchPanelView = ({
  side,
  children,
  expanded,
  onToggle,
  pointsLeft,
  width,
}: CollapsiblePitchPanelViewProps) => (
  // `relative`: the handle is absolute inside the wrapper, so the pitch
  // column sits flush against the panel (W06) instead of behind a gutter.
  <Stack
    direction="horizontal"
    testID={`pitch-panel-wrapper-${side}`}
    className="relative z-10 shrink-0"
  >
    <Box
      testID={`collapsible-pitch-panel-${side}`}
      aria-hidden={!expanded}
      className={`overflow-hidden transition-all duration-300 ease-in-out motion-reduce:transition-none ${expanded ? `visible ${EXPANDED_WIDTH[width]}` : "w-0 invisible"}`}
    >
      {/* SE-8526: each Pitch column scrolls on its own inside the viewport
          (Figma 314:4924 clips three equal-height columns), so a long panel
          no longer stretches the page beside the pitch. */}
      <ScrollView
        testID={`pitch-panel-scroll-${side}`}
        className="min-h-0 flex-1"
        contentContainerClassName="flex-grow"
      >
        {children}
      </ScrollView>
    </Box>
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${expanded ? "Collapse" : "Expand"} ${side} panel`}
      accessibilityState={{ expanded }}
      onPress={onToggle}
      className={`absolute top-1/2 z-10 size-7 items-center justify-center overflow-hidden rounded-sm border border-outline-default bg-surface-base shadow-sm -translate-y-1/2 hover:bg-surface-subtle focus-visible:ring-2 focus-visible:ring-outline-default ${
        HANDLE_PLACEMENT[side][expanded ? "expanded" : "collapsed"]
      }`}
    >
      <Icon
        as={pointsLeft ? ChevronsLeftIcon : ChevronsRightIcon}
        size="xs"
        className="text-content-muted"
      />
    </Pressable>
  </Stack>
);

CollapsiblePitchPanelView.displayName = "CollapsiblePitchPanelView";
export default memo(CollapsiblePitchPanelView);
